import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';

const UserProfile = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [headshotFile, setHeadshotFile] = useState(null);
  const [fullBodyFile, setFullBodyFile] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [fullBodyPreview, setFullBodyPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null); // 'headshot' or 'fullbody' or null
  const [userPhotos, setUserPhotos] = useState({ headshot: null, fullbody: null });
  const [imageVersion, setImageVersion] = useState(Date.now()); // Cache-busting for images
  const [imageRefreshKey, setImageRefreshKey] = useState(0); // Force image element refresh
  const [headshotRetryCount, setHeadshotRetryCount] = useState(0);
  const [fullbodyRetryCount, setFullbodyRetryCount] = useState(0);
  const [headshotLoadError, setHeadshotLoadError] = useState(false);
  const [fullbodyLoadError, setFullbodyLoadError] = useState(false);
  const headshotInputRef = useRef(null);
  const fullbodyInputRef = useRef(null);
  const headshotImageRef = useRef(null);
  const fullbodyImageRef = useRef(null);

  useEffect(() => {
    console.log('🔄 UserProfile component mounted - fetching profiles and user photos...');
    fetchProfiles();
  }, []);

  // Force image refresh when userPhotos changes
  useEffect(() => {
    if (userPhotos.headshot || userPhotos.fullbody) {
      console.log('🔄 userPhotos changed, forcing image refresh:', userPhotos);
      console.log('🔄 Headshot URL:', userPhotos.headshot);
      console.log('🔄 Fullbody URL:', userPhotos.fullbody);
      // Force image refresh by updating version and refresh key
      const newVersion = Date.now();
      setImageVersion(newVersion);
      setImageRefreshKey(prev => {
        const newKey = prev + 1;
        
        // Force image reload using refs after state updates
        setTimeout(() => {
          // Force headshot image reload
          if (userPhotos.headshot && headshotImageRef.current) {
            let baseUrl = userPhotos.headshot.split('?')[0];
            // Ensure full URL
            if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
              if (baseUrl.startsWith('/')) {
                baseUrl = window.location.origin + baseUrl;
              } else {
                baseUrl = window.location.origin + '/' + baseUrl;
              }
            }
            const newUrl = baseUrl + '?v=' + Date.now() + '&r=' + newKey;
            console.log('🔄 Forcing headshot reload with URL:', newUrl);
            console.log('🔄 Headshot image ref exists:', !!headshotImageRef.current);
            headshotImageRef.current.src = '';
            setTimeout(() => {
              if (headshotImageRef.current) {
                headshotImageRef.current.src = newUrl;
                console.log('✅ Headshot image src set to:', newUrl);
              } else {
                console.warn('⚠️ Headshot image ref is null after timeout');
              }
            }, 50); // Increased delay to ensure ref is ready
          } else if (userPhotos.headshot) {
            console.warn('⚠️ Headshot URL exists but image ref is null:', userPhotos.headshot);
          }
          
          // Force fullbody image reload
          if (userPhotos.fullbody && fullbodyImageRef.current) {
            let baseUrl = userPhotos.fullbody.split('?')[0];
            // Ensure full URL
            if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
              if (baseUrl.startsWith('/')) {
                baseUrl = window.location.origin + baseUrl;
              } else {
                baseUrl = window.location.origin + '/' + baseUrl;
              }
            }
            const newUrl = baseUrl + '?v=' + Date.now() + '&r=' + newKey;
            console.log('🔄 Forcing fullbody reload with URL:', newUrl);
            console.log('🔄 Fullbody image ref exists:', !!fullbodyImageRef.current);
            fullbodyImageRef.current.src = '';
            setTimeout(() => {
              if (fullbodyImageRef.current) {
                fullbodyImageRef.current.src = newUrl;
                console.log('✅ Fullbody image src set to:', newUrl);
              } else {
                console.warn('⚠️ Fullbody image ref is null after timeout');
              }
            }, 50); // Increased delay to ensure ref is ready
          } else if (userPhotos.fullbody) {
            console.warn('⚠️ Fullbody URL exists but image ref is null:', userPhotos.fullbody);
          }
        }, 100);
        
        return newKey;
      });
    }
  }, [userPhotos.headshot, userPhotos.fullbody]);

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Fetching profiles with token:', token.substring(0, 50) + '...');
      
      // Set cookie as fallback (cookies are more reliable than headers on some hosts)
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/talent-profiles`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token  // Fallback header
          },
          withCredentials: true  // Important for cookies
        });
      } catch (err) {
        // If 401, try with query parameter as last resort
        if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          response = await axios.get(`${API_BASE_URL}/talent-profiles`, {
            params: { token: token },
            headers: { 
              'X-Auth-Token': token  // Still send custom header
            },
            withCredentials: true
          });
        } else {
          throw err;
        }
      }
      
      // Ensure we always set an array
      const profilesData = Array.isArray(response.data) ? response.data : [];
      setProfiles(profilesData);
      
      // Fetch user's photos from talents table
      // Add cache-busting parameter to ensure we get fresh data
      try {
        const userResponse = await axios.get(`${API_BASE_URL}/auth/user`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          params: {
            _t: Date.now() // Cache-busting parameter
          },
          withCredentials: true
        });
        
        if (userResponse.data) {
          console.log('📥 User data received from API:', {
            'headshot_url': userResponse.data.headshot_url,
            'fullbody_url': userResponse.data.fullbody_url,
            'headshot_exists': !!userResponse.data.headshot_url,
            'fullbody_exists': !!userResponse.data.fullbody_url
          });
          
          // CRITICAL: Always update photos from database on page load/refresh
          // Database is the source of truth - always use server URLs
          setUserPhotos(prevPhotos => {
            console.log('📥 Current userPhotos state:', prevPhotos);
            
            // Normalize URLs to ensure they're absolute
            const normalizeUrl = (url) => {
              if (!url) return null;
              // Remove any existing query parameters
              let baseUrl = url.split('?')[0].trim();
              // If URL is relative, make it absolute
              if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                if (baseUrl.startsWith('/')) {
                  baseUrl = window.location.origin + baseUrl;
                } else {
                  baseUrl = window.location.origin + '/' + baseUrl;
                }
              }
              return baseUrl;
            };
            
            const serverHeadshotUrl = normalizeUrl(userResponse.data.headshot_url);
            const serverFullbodyUrl = normalizeUrl(userResponse.data.fullbody_url);
            
            // ALWAYS use server URLs (database is source of truth)
            // This ensures images always show correctly after page refresh
            const newPhotos = {
              headshot: serverHeadshotUrl || null,
              fullbody: serverFullbodyUrl || null
            };
            
            console.log('🔄 fetchProfiles - Updating userPhotos from database:', {
              'server_headshot_raw': userResponse.data.headshot_url,
              'server_headshot_normalized': serverHeadshotUrl,
              'server_fullbody_raw': userResponse.data.fullbody_url,
              'server_fullbody_normalized': serverFullbodyUrl,
              'prev_headshot': prevPhotos.headshot,
              'prev_fullbody': prevPhotos.fullbody,
              'new_headshot': newPhotos.headshot,
              'new_fullbody': newPhotos.fullbody,
              'headshot_changed': newPhotos.headshot !== prevPhotos.headshot,
              'fullbody_changed': newPhotos.fullbody !== prevPhotos.fullbody
            });
            
            // Only update if URLs actually changed to avoid unnecessary re-renders
            if (newPhotos.headshot !== prevPhotos.headshot || newPhotos.fullbody !== prevPhotos.fullbody) {
              console.log('✅ Updating userPhotos state with new URLs');
              return newPhotos;
            }
            
            // No change needed
            console.log('ℹ️ userPhotos unchanged, skipping update');
            return prevPhotos;
          });
          // Update image version to force refresh of images
          const newVersion = Date.now();
          setImageVersion(newVersion);
          setImageRefreshKey(prev => prev + 1);
          console.log('fetchProfiles - Updated imageVersion to:', newVersion);
        }
      } catch (err) {
        console.log('Could not fetch user photos:', err);
        // Not critical, continue
      }
      
      setError('');
    } catch (err) {
      console.error('Failed to fetch talent profiles:', err);
      console.error('Error response:', err.response?.data);
      if (err.response?.status === 401) {
        const debugInfo = err.response?.data?.debug;
        if (debugInfo) {
          console.error('Debug info:', debugInfo);
          setError(`Authentication failed. Token source: ${debugInfo.token_source || 'none'}. Please log in again.`);
        } else {
          setError('Session expired. Please log in again.');
        }
      } else if (err.response?.status === 404) {
        setError('');
        setProfiles([]);
      } else {
        setError('Failed to fetch talent profiles. Please try again later.');
        setProfiles([]); // Ensure profiles is always an array
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteProfile = async (id) => {
    if (window.confirm('Are you sure you want to delete this talent profile?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/talent-profiles/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchProfiles();
      } catch (err) {
        console.error('Failed to delete talent profile:', err);
        setError('Failed to delete talent profile');
      }
    }
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'headshot') {
        setHeadshotFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setHeadshotPreview(e.target.result);
        reader.readAsDataURL(file);
        
        // Auto-upload immediately when file is selected
        await handleSingleImageUpload(file, 'headshot');
      } else if (type === 'fullbody') {
        console.log('📁 Fullbody file selected:', file.name, file.size, 'bytes');
        setFullBodyFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setFullBodyPreview(e.target.result);
        reader.readAsDataURL(file);
        
        // Auto-upload immediately when file is selected
        console.log('🚀 Starting automatic fullbody upload...');
        await handleSingleImageUpload(file, 'fullbody');
        console.log('✅ Fullbody upload completed');
      }
      
      // Reset the input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleSingleImageUpload = async (file, type) => {
    if (!file) return;

    console.log(`🚀 Starting ${type} upload...`, { fileName: file.name, fileSize: file.size, fileType: file.type });
    
    setUploading(true);
    setUploadingType(type);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append(type, file);

      // Set cookie as fallback
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      console.log(`📤 Uploading ${type} file:`, { fileName: file.name, fileSize: file.size });
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      let response;
      try {
        response = await axios.post(`${API_BASE_URL}/upload-photos`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token,
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true,
          timeout: 60000, // 60 second timeout for file uploads
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        });
        console.log(`✅ ${type} upload response received:`, response.data);
      } catch (err) {
        // If 401, try with query parameter as last resort
        if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          response = await axios.post(`${API_BASE_URL}/upload-photos?token=${token}`, formData, {
            headers: { 
              'X-Auth-Token': token,
              'Content-Type': 'multipart/form-data'
            },
            withCredentials: true,
            timeout: 60000, // 60 second timeout for file uploads
            maxContentLength: Infinity,
            maxBodyLength: Infinity
          });
          console.log(`✅ ${type} upload response received (with query param):`, response.data);
        } else {
          console.error(`❌ ${type} upload failed:`, err);
          throw err;
        }
      }

        // Log the full response for debugging
        console.log('📦 Full API response:', response.data);
        console.log('📦 Response files:', response.data?.files);
        console.log('📦 Files count:', response.data?.files ? Object.keys(response.data.files).length : 0);
        console.log('📦 Files keys:', response.data?.files ? Object.keys(response.data.files) : []);
        
        // Check if database was updated successfully
        if (response?.data?.debug?.database_updated === false) {
          console.error('❌ Database update failed:', response.data.debug.database_error);
          setError('Photo uploaded but failed to save to database. Please try again.');
          return;
        }
        
        // Clear any previous errors immediately on successful response
        setError('');
        
        // Log database update status
        if (response?.data?.debug) {
          console.log('📊 Database update status:', {
            'database_updated': response.data.debug.database_updated,
            'rows_affected': response.data.debug.rows_affected,
            'database_error': response.data.debug.database_error
          });
        }
        
        // Update userPhotos state with uploaded URL immediately (store base URL, add cache-busting on render)
        // Check for files in response - it might be empty array or missing
        const responseFiles = response?.data?.files;
        const databaseUpdated = response?.data?.database_updated === true || response?.data?.debug?.database_updated === true;
        
        // If database was updated, we should always try to fetch the latest URLs
        // Even if files are missing from response, the database has the URLs
        if (responseFiles && typeof responseFiles === 'object' && Object.keys(responseFiles).length > 0) {
        const newVersion = Date.now(); // Generate version before state update
        const newRefreshKey = imageRefreshKey + 1; // Force complete image element refresh
        
        console.log('✅ Upload successful! Files received:', Object.keys(response.data.files));
        console.log('📦 Full response data:', response.data);
        
        // Clear previews first
        if (response.data.files.headshot) {
          console.log('📸 Headshot file in response:', response.data.files.headshot);
          setHeadshotFile(null);
          setHeadshotPreview(null);
        }
        if (response.data.files.fullbody) {
          console.log('📸 Fullbody file in response:', response.data.files.fullbody);
          setFullBodyFile(null);
          setFullBodyPreview(null);
        }
        
        // Normalize URLs helper function
        const normalizeUrl = (url) => {
          if (!url) return null;
          let baseUrl = url.split('?')[0];
          if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
            if (baseUrl.startsWith('/')) {
              baseUrl = window.location.origin + baseUrl;
            } else {
              baseUrl = window.location.origin + '/' + baseUrl;
            }
          }
          return baseUrl;
        };
        
        // Update state immediately with new URL - this triggers re-render
        setUserPhotos(prevPhotos => {
          const newPhotos = { ...prevPhotos };
          if (response.data.files.headshot) {
            // Store base URL without cache-busting, but normalize it
            const headshotUrl = normalizeUrl(response.data.files.headshot.url);
            if (headshotUrl) {
              newPhotos.headshot = headshotUrl;
              console.log('✅ Setting headshot URL in state:', headshotUrl);
              // Reset retry counters and error state for headshot
              setHeadshotRetryCount(0);
              setHeadshotLoadError(false);
            }
          }
          if (response.data.files.fullbody) {
            // Store base URL without cache-busting, but normalize it
            const fullbodyUrl = normalizeUrl(response.data.files.fullbody.url);
            const prevFullbodyUrl = prevPhotos.fullbody;
            if (fullbodyUrl) {
              newPhotos.fullbody = fullbodyUrl;
              console.log('✅ Setting fullbody URL in state:', fullbodyUrl);
              console.log('✅ Previous fullbody URL was:', prevFullbodyUrl);
              console.log('✅ Fullbody URL changed:', prevFullbodyUrl !== fullbodyUrl);
              console.log('✅ Fullbody URL will be used on next render');
              // Reset retry counters and error state for fullbody
              setFullbodyRetryCount(0);
              setFullbodyLoadError(false);
            }
          }
          console.log('📊 Updated userPhotos state:', newPhotos);
          return newPhotos;
        });
        
        // Force complete image refresh - update both version and refresh key
        setImageVersion(newVersion);
        setImageRefreshKey(newRefreshKey);
        
        // Force image element to reload by updating src directly after a brief delay
        // This ensures the file is available on the server
        setTimeout(() => {
          if (response.data.files.headshot && headshotImageRef.current) {
            const newUrl = response.data.files.headshot.url + '?v=' + Date.now() + '&r=' + (imageRefreshKey + 1);
            // Force browser to reload by setting src to empty first, then new URL
            headshotImageRef.current.src = '';
            setTimeout(() => {
              if (headshotImageRef.current) {
                headshotImageRef.current.src = newUrl;
              }
            }, 10);
          }
          if (response.data.files.fullbody && fullbodyImageRef.current) {
            const newUrl = response.data.files.fullbody.url + '?v=' + Date.now() + '&r=' + (imageRefreshKey + 1);
            console.log('🔄 Forcing fullbody image reload with URL:', newUrl);
            // Force browser to reload by setting src to empty first, then new URL
            fullbodyImageRef.current.src = '';
            setTimeout(() => {
              if (fullbodyImageRef.current) {
                fullbodyImageRef.current.src = newUrl;
                console.log('✅ Fullbody image src updated to:', newUrl);
              }
            }, 10);
          }
          
          // Update version again to force React re-render
          setImageVersion(Date.now());
          setImageRefreshKey(prev => prev + 1);
        }, 150);
        
        // Show success message after state has updated
        setTimeout(() => {
          const imageType = type === 'headshot' ? 'Headshot' : 'Full body photo';
          console.log(`✅ ${imageType} updated successfully!`);
          // Clear any error messages and show success
          setError('');
          setSuccess(`${imageType} updated successfully!`);
          // Auto-clear success message after 5 seconds
          setTimeout(() => {
            setSuccess('');
          }, 5000);
        }, 200);
        
        // CRITICAL: After upload, wait for database to commit, then fetch latest from database
        // This ensures we have the correct URL that will persist after page refresh
        setTimeout(() => {
          console.log('🔄 Fetching latest user data from database after upload to ensure persistence...');
          fetchProfiles();
        }, 2000); // Wait 2 seconds to ensure database commit
      } else {
        // Response doesn't have files - this shouldn't happen but handle it
        console.warn('⚠️ Upload response received but no files in response:', response.data);
        console.warn('⚠️ Response structure:', {
          hasFiles: !!response?.data?.files,
          filesType: typeof response?.data?.files,
          filesIsArray: Array.isArray(response?.data?.files),
          filesKeys: response?.data?.files ? Object.keys(response.data.files) : [],
          filesCount: response?.data?.files ? Object.keys(response.data.files).length : 0,
          message: response?.data?.message,
          saved: response?.data?.saved,
          database_updated: response?.data?.database_updated || response?.data?.debug?.database_updated,
          count: response?.data?.count,
          fullResponse: response?.data
        });
        
        // ALWAYS try to fetch user data if files are missing - the upload might have succeeded
        // even if the files object is missing from the response
        // This is a fallback to ensure we get the photo URLs from the database
        console.log('🔄 Files missing from response - fetching user data as fallback...');
        
        // Check if database was updated (check both locations)
        const databaseUpdated = response?.data?.database_updated === true || response?.data?.debug?.database_updated === true;
        
        // Check if the upload appears successful based on various indicators
        const uploadSucceeded = (response?.data?.message && (response.data.message.includes('successfully') || response.data.message.includes('uploaded'))) 
                              || response?.data?.saved === true 
                              || databaseUpdated
                              || response?.data?.count > 0
                              || (response?.status === 200 && !response?.data?.error);
        
        // If database was updated, show success message immediately and fetch URLs
        if (databaseUpdated) {
          const imageType = type === 'headshot' ? 'Headshot' : 'Full body photo';
          setSuccess(`${imageType} uploaded successfully! Fetching latest image...`);
          setTimeout(() => setSuccess(''), 5000);
        }
        
        // ALWAYS try to fetch user data if status is 200 or database was updated
        // This ensures we get the latest photo URLs from the database even if response structure is wrong
        if (uploadSucceeded || response?.status === 200 || databaseUpdated) {
          console.log('✅ Upload appears successful (status 200), fetching user data to get photo URLs...');
          // Try to fetch user data to get updated photo URLs
          // Use a longer delay to ensure database has fully updated
          setTimeout(async () => {
            try {
              const token = localStorage.getItem('token');
              console.log('🔄 Fetching user data to get updated photo URLs...');
              const userResponse = await axios.get(`${API_BASE_URL}/auth/user`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { _t: Date.now() }
              });
              console.log('📥 User data received:', userResponse.data);
              
              // Update photos from user data
              if (userResponse.data) {
                // Normalize URLs to ensure they're absolute
                const normalizeUrl = (url) => {
                  if (!url) return null;
                  let baseUrl = url.split('?')[0];
                  if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                    if (baseUrl.startsWith('/')) {
                      baseUrl = window.location.origin + baseUrl;
                    } else {
                      baseUrl = window.location.origin + '/' + baseUrl;
                    }
                  }
                  return baseUrl;
                };
                
                // Use functional state update to ensure we have the latest state
                setUserPhotos(prevPhotos => {
                  const updatedPhotos = { ...prevPhotos };
                  let hasUpdate = false;
                  
                  // Only update the photo type that was uploaded
                  if (type === 'headshot' && userResponse.data.headshot_url) {
                    const normalizedUrl = normalizeUrl(userResponse.data.headshot_url);
                    if (normalizedUrl && normalizedUrl !== updatedPhotos.headshot) {
                      updatedPhotos.headshot = normalizedUrl;
                      hasUpdate = true;
                      console.log('✅ Retrieved headshot URL from user endpoint:', normalizedUrl);
                    }
                  }
                  
                  if (type === 'fullbody' && userResponse.data.fullbody_url) {
                    const normalizedUrl = normalizeUrl(userResponse.data.fullbody_url);
                    if (normalizedUrl && normalizedUrl !== updatedPhotos.fullbody) {
                      updatedPhotos.fullbody = normalizedUrl;
                      hasUpdate = true;
                      console.log('✅ Retrieved fullbody URL from user endpoint:', normalizedUrl);
                    }
                  }
                  
                  if (hasUpdate) {
                    console.log('✅ Updating userPhotos with URLs from user endpoint:', updatedPhotos);
                    return updatedPhotos;
                  }
                  
                  return prevPhotos; // No change
                });
                
                // If we got URLs, update image version and force reload
                const hasHeadshot = type === 'headshot' && userResponse.data.headshot_url;
                const hasFullbody = type === 'fullbody' && userResponse.data.fullbody_url;
                
                if (hasHeadshot || hasFullbody) {
                  setImageVersion(Date.now());
                  setImageRefreshKey(prev => prev + 1);
                  
                  // Force image reload after state update
                  setTimeout(() => {
                    if (type === 'headshot' && headshotImageRef.current && userResponse.data.headshot_url) {
                      const normalizedUrl = normalizeUrl(userResponse.data.headshot_url);
                      const newUrl = normalizedUrl + '?v=' + Date.now();
                      headshotImageRef.current.src = '';
                      setTimeout(() => {
                        if (headshotImageRef.current) {
                          headshotImageRef.current.src = newUrl;
                          console.log('🔄 Forced headshot image reload with URL:', newUrl);
                        }
                      }, 10);
                    }
                    if (type === 'fullbody' && fullbodyImageRef.current && userResponse.data.fullbody_url) {
                      const normalizedUrl = normalizeUrl(userResponse.data.fullbody_url);
                      const newUrl = normalizedUrl + '?v=' + Date.now();
                      fullbodyImageRef.current.src = '';
                      setTimeout(() => {
                        if (fullbodyImageRef.current) {
                          fullbodyImageRef.current.src = newUrl;
                          console.log('🔄 Forced fullbody image reload with URL:', newUrl);
                        }
                      }, 10);
                    }
                  }, 100);
                  
                  const imageType = type === 'headshot' ? 'Headshot' : 'Full body photo';
                  setSuccess(`${imageType} uploaded successfully!`);
                  setTimeout(() => setSuccess(''), 5000);
                  setError(''); // Clear any error
                  console.log('✅ Photo URLs updated from user endpoint');
                  
                  // Also call fetchProfiles to ensure everything is synced
                  setTimeout(() => {
                    console.log('🔄 Calling fetchProfiles to sync all data...');
                    fetchProfiles();
                  }, 500);
                } else {
                  console.warn('⚠️ No photo URLs found in user data after upload for type:', type);
                  console.warn('⚠️ User data:', userResponse.data);
                  // If database was updated, show success message and try fetchProfiles
                  if (databaseUpdated) {
                    const imageType = type === 'headshot' ? 'Headshot' : 'Full body photo';
                    setSuccess(`${imageType} uploaded successfully! Fetching latest images...`);
                    setTimeout(() => setSuccess(''), 5000);
                    setError(''); // Clear any error
                    
                    // Try fetchProfiles to get the latest URLs - database was updated so they should be there
                    setTimeout(() => {
                      console.log('🔄 Database was updated but URLs not found in initial fetch. Calling fetchProfiles to get latest data...');
                      fetchProfiles();
                    }, 1000); // Wait 1 more second then fetch
                  } else {
                    // Still clear the error since upload might have succeeded
                    setError('Upload may have completed. Please refresh the page to verify.');
                    // Even if databaseUpdated is false, try fetchProfiles as a last resort
                    setTimeout(() => {
                      console.log('🔄 Upload may have completed. Trying fetchProfiles to check for new images...');
                      fetchProfiles();
                    }, 2000);
                  }
                }
              } else {
                // No user data received - try fetchProfiles anyway if database was updated
                if (databaseUpdated) {
                  const imageType = type === 'headshot' ? 'Headshot' : 'Full body photo';
                  setSuccess(`${imageType} uploaded successfully! Fetching latest images...`);
                  setTimeout(() => setSuccess(''), 5000);
                  setError('');
                  
                  setTimeout(() => {
                    console.log('🔄 Database was updated. Calling fetchProfiles to get latest data...');
                    fetchProfiles();
                  }, 1000);
                }
              }
            } catch (fetchErr) {
              console.error('Failed to fetch updated user data:', fetchErr);
              // If database was updated, still try fetchProfiles
              if (databaseUpdated) {
                setSuccess('Upload completed! Fetching latest images...');
                setTimeout(() => setSuccess(''), 5000);
                setError('');
                
                setTimeout(() => {
                  console.log('🔄 Fetch failed but database was updated. Trying fetchProfiles...');
                  fetchProfiles();
                }, 1000);
              } else {
                setError('Upload completed but could not verify. Please refresh the page to verify.');
                // Try fetchProfiles anyway as last resort
                setTimeout(() => {
                  console.log('🔄 Trying fetchProfiles as last resort...');
                  fetchProfiles();
                }, 2000);
              }
            }
          }, 2000); // Wait 2 seconds to ensure database has fully committed
        } else {
          // If status is not 200, show error immediately
          console.error('❌ Upload failed - status:', response?.status);
          setError('Upload failed. Please try again.');
        }
      }
      
      // DO NOT refresh profiles immediately after upload
      // The fetchProfiles() call was overwriting the newly uploaded images with stale data
      // The images are already updated in state above, so no need to refresh
      console.log('✅ Upload complete - NOT calling fetchProfiles() immediately to prevent overwriting new images');
      
      // The newly uploaded images are already in state and will persist
      // No need to refresh - the images are correct and saved to database
    } catch (err) {
      console.error('Photo upload error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error response text:', err.response?.data ? JSON.stringify(err.response.data, null, 2) : 'No response data');
      console.error('Error status:', err.response?.status);
      console.error('Error headers:', err.response?.headers);
      console.error('Full error object:', err);
      console.error('Error code:', err.code);
      console.error('API URL attempted:', `${API_BASE_URL}/upload-photos`);
      
      // Check if upload actually succeeded despite error response
      // Sometimes the file uploads but there's a minor error in response handling
      if (err.response?.data?.uploaded_files && Object.keys(err.response.data.uploaded_files).length > 0) {
        console.log('Upload succeeded despite error response, files:', err.response.data.uploaded_files);
        // Don't show error if files were actually uploaded
        return;
      }
      
      // Handle network errors (no response object)
      if (!err.response) {
        const isNetworkError = err.message === 'Network Error' || 
                              err.message?.includes('Network Error') ||
                              err.code === 'ERR_NETWORK' ||
                              err.code === 'ECONNABORTED' ||
                              err.code === 'ETIMEDOUT';
        
        if (isNetworkError) {
          console.error('❌ Network error detected:', {
            message: err.message,
            code: err.code,
            apiUrl: `${API_BASE_URL}/upload-photos`,
            isTimeout: err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
          });
          
          let errorMsg = 'Network connection failed. ';
          
          if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
            errorMsg += 'The request timed out. This may be due to a slow connection or large file size. ';
          } else if (err.code === 'ERR_NETWORK') {
            errorMsg += 'Unable to reach the server. ';
          }
          
          errorMsg += 'Please check:\n';
          errorMsg += '1. Your internet connection\n';
          errorMsg += '2. The server is accessible\n';
          errorMsg += '3. File size is not too large\n';
          errorMsg += '4. Try again in a few moments';
          
          setError(errorMsg);
          return;
        }
      }
      
      if (err.response?.status === 401) {
        const debugInfo = err.response?.data?.debug;
        if (debugInfo) {
          console.error('Debug info:', debugInfo);
          setError(`Authentication failed. Token source: ${debugInfo.token_source || 'none'}. Please log in again.`);
        } else {
          setError('Session expired. Please log in again.');
        }
      } else if (err.response?.status === 413) {
        setError('File too large. Maximum size is 5MB per file.');
      } else if (err.response?.status === 400) {
        // Only show error if it's not the "No files uploaded" message when we know a file was sent
        const errorMsg = err.response?.data?.message || 'Invalid file. Please ensure files are images (JPEG, PNG, or GIF).';
        
        // Don't show "No files uploaded" error if we just sent a file
        if (errorMsg.includes('No files were uploaded') && file) {
          console.warn('Received "No files uploaded" error but file was sent. This may be a false error.');
          // Don't set error - the upload might have actually succeeded
          return;
        }
        
        setError(errorMsg);
      } else if (err.response?.status === 500) {
        // Server error - show more detailed message
        const errorData = err.response?.data;
        let errorMsg = 'Server error occurred. ';
        
        if (errorData) {
          errorMsg += errorData.message || errorData.error || 'Unknown server error.';
          
          // Add more details if available
          if (errorData.error_details) {
            errorMsg += ` Details: ${errorData.error_details}`;
          }
          if (errorData.error_type) {
            errorMsg += ` (${errorData.error_type})`;
          }
          if (errorData.file && errorData.line) {
            errorMsg += ` [${errorData.file}:${errorData.line}]`;
          }
        } else {
          // No error data in response - this means the server crashed before sending JSON
          errorMsg += 'The server encountered an error before it could provide details. Please check the server error logs in cPanel.';
          console.error('⚠️ 500 error with no response data - server may have crashed or output was sent before headers');
        }
        
        setError(`Server error: ${errorMsg}`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(`Failed to upload photo. ${err.message || 'Please try again.'}`);
      }
    } finally {
      setUploading(false);
      setUploadingType(null);
    }
  };

  const handleEditIconClick = (type) => {
    if (type === 'headshot' && headshotInputRef.current) {
      headshotInputRef.current.click();
    } else if (type === 'fullbody' && fullbodyInputRef.current) {
      fullbodyInputRef.current.click();
    }
  };

  const handlePhotoUpload = async () => {
    if (!headshotFile && !fullBodyFile) {
      setError('Please select at least one photo to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (headshotFile) {
        formData.append('headshot', headshotFile);
      }
      if (fullBodyFile) {
        formData.append('fullbody', fullBodyFile);
      }

      // Set cookie as fallback (cookies are more reliable than headers on some hosts)
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      let response;
      try {
        response = await axios.post(`${API_BASE_URL}/upload-photos`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token,  // Fallback header
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true,  // Important for cookies
          timeout: 60000, // 60 second timeout for file uploads
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        });
      } catch (err) {
        // If 401, try with query parameter as last resort
        if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          response = await axios.post(`${API_BASE_URL}/upload-photos?token=${token}`, formData, {
            headers: { 
              'X-Auth-Token': token,
              'Content-Type': 'multipart/form-data'
            },
            withCredentials: true,
            timeout: 60000, // 60 second timeout for file uploads
            maxContentLength: Infinity,
            maxBodyLength: Infinity
          });
        } else {
          throw err;
        }
      }

      // Check response for debug info
      if (response?.data?.debug) {
        console.log('Upload debug info:', response.data.debug);
      }
      
      // Update userPhotos state with uploaded URLs (store base URL, add cache-busting on render)
      if (response?.data?.files) {
        const newPhotos = { ...userPhotos };
        if (response.data.files.headshot) {
          // Store base URL without cache-busting
          newPhotos.headshot = response.data.files.headshot.url;
        }
        if (response.data.files.fullbody) {
          // Store base URL without cache-busting
          newPhotos.fullbody = response.data.files.fullbody.url;
        }
        setUserPhotos(newPhotos);
        // Force image refresh
        setImageVersion(Date.now());
      }
      
      // Reset file inputs
      setHeadshotFile(null);
      setFullBodyFile(null);
      setHeadshotPreview(null);
      setFullBodyPreview(null);
      
      // Refresh profiles to show uploaded photos
      await fetchProfiles();
      
      // Force image refresh again after fetching
      setImageVersion(Date.now());
      
      // Show success message
      setSuccess('Photos uploaded successfully!');
      setError('');
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
    } catch (err) {
      console.error('Photo upload error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error code:', err.code);
      console.error('API URL attempted:', `${API_BASE_URL}/upload-photos`);
      
      // Handle network errors (no response object)
      if (!err.response) {
        const isNetworkError = err.message === 'Network Error' || 
                              err.message?.includes('Network Error') ||
                              err.code === 'ERR_NETWORK' ||
                              err.code === 'ECONNABORTED' ||
                              err.code === 'ETIMEDOUT';
        
        if (isNetworkError) {
          console.error('❌ Network error detected:', {
            message: err.message,
            code: err.code,
            apiUrl: `${API_BASE_URL}/upload-photos`,
            isTimeout: err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
          });
          
          let errorMsg = 'Network connection failed. ';
          
          if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
            errorMsg += 'The request timed out. This may be due to a slow connection or large file size. ';
          } else if (err.code === 'ERR_NETWORK') {
            errorMsg += 'Unable to reach the server. ';
          }
          
          errorMsg += 'Please check:\n';
          errorMsg += '1. Your internet connection\n';
          errorMsg += '2. The server is accessible\n';
          errorMsg += '3. File size is not too large\n';
          errorMsg += '4. Try again in a few moments';
          
          setError(errorMsg);
          setUploading(false);
          return;
        }
      }
      
      if (err.response?.status === 401) {
        const debugInfo = err.response?.data?.debug;
        if (debugInfo) {
          console.error('Debug info:', debugInfo);
          setError(`Authentication failed. Token source: ${debugInfo.token_source || 'none'}. Please log in again.`);
        } else {
          setError('Session expired. Please log in again.');
        }
      } else if (err.response?.status === 413) {
        setError('File too large. Maximum size is 5MB per file.');
      } else if (err.response?.status === 400) {
        const errorMsg = err.response?.data?.message || 'Invalid file. Please ensure files are images (JPEG, PNG, or GIF).';
        setError(errorMsg);
      } else if (err.response?.status === 500) {
        const errorData = err.response?.data;
        let errorMsg = 'Server error occurred. ';
        if (errorData) {
          errorMsg += errorData.message || errorData.error || 'Unknown server error.';
        } else {
          errorMsg += 'The server encountered an error. Please try again later.';
        }
        setError(`Server error: ${errorMsg}`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(`Failed to upload photos. ${err.message || 'Please try again.'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading talent profiles...</div>;
  }

  const handleLogout = () => {
    // Clear auth data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
    
    // Call parent logout handler if provided
    if (onLogout) {
      onLogout();
    }
    
    // Navigate to home
    navigate('/');
  };

  return (
    <div style={{ padding: '0 10px' }}>
      <div className="dashboard-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
        <h2 style={{ flex: '1', margin: 0, minWidth: '200px' }}>My Manhattanhanover Talent Portfolio</h2>
        <button 
          onClick={handleLogout}
          style={{ 
            background: '#dc3545',
            border: '1px solid #dc3545',
            color: '#fff',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#c82333';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 2px 8px rgba(220, 53, 69, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#dc3545';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Logout
        </button>
      </div>

      {/* Photo Upload Section */}
      <div className="photo-upload-section">
        <h3>Upload Your Photos</h3>
        <div className="upload-grid">
          <div className="upload-item">
            <div className="upload-box-wrapper">
              <label className="upload-label">
                <div className="upload-box">
                  {headshotPreview ? (
                    <img src={headshotPreview} alt="Headshot preview" className="preview-image" key={`headshot-preview-${imageVersion}`} />
                  ) : userPhotos.headshot ? (
                    <img 
                      ref={headshotImageRef}
                      src={(() => {
                        if (!userPhotos.headshot) return '';
                        // Ensure we have a valid URL
                        let baseUrl = userPhotos.headshot;
                        // Remove any existing query parameters
                        if (baseUrl.includes('?')) {
                          baseUrl = baseUrl.split('?')[0];
                        }
                        // Ensure it's a full URL (add protocol if missing)
                        if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                          // If it's a relative URL, make it absolute
                          if (baseUrl.startsWith('/')) {
                            baseUrl = window.location.origin + baseUrl;
                          } else {
                            baseUrl = window.location.origin + '/' + baseUrl;
                          }
                        }
                        const finalUrl = baseUrl + '?v=' + imageVersion + '&r=' + imageRefreshKey;
                        console.log('Headshot image URL:', finalUrl);
                        return finalUrl;
                      })()}
                      alt="Headshot" 
                      className="preview-image" 
                      key={`headshot-${imageVersion}-${imageRefreshKey}-${userPhotos.headshot}`}
                      style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
                      onLoad={() => {
                        // Image loaded successfully
                        console.log('✅ Headshot image loaded successfully:', userPhotos.headshot);
                        setHeadshotLoadError(false);
                        setHeadshotRetryCount(0);
                        if (headshotImageRef.current) {
                          headshotImageRef.current.style.opacity = '1';
                        }
                      }}
                      onError={(e) => {
                        // Limit retries to prevent infinite loops
                        if (headshotRetryCount < 2) {
                          console.error('❌ Headshot image failed to load, retrying...', userPhotos.headshot);
                          setHeadshotRetryCount(prev => prev + 1);
                          if (userPhotos.headshot) {
                            let baseUrl = userPhotos.headshot.split('?')[0];
                            if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                              if (baseUrl.startsWith('/')) {
                                baseUrl = window.location.origin + baseUrl;
                              } else {
                                baseUrl = window.location.origin + '/' + baseUrl;
                              }
                            }
                            const retryUrl = baseUrl + '?v=' + Date.now() + '&r=' + (imageRefreshKey + 1);
                            console.log('Retrying with URL:', retryUrl);
                            e.target.src = retryUrl;
                          }
                        } else {
                          // Max retries reached, show error state
                          console.error('❌ Headshot image failed to load after retries. File may not exist:', userPhotos.headshot);
                          console.error('❌ This might be an old image URL. Please upload a new image.');
                          setHeadshotLoadError(true);
                          // Clear the failed URL so user can upload a new one
                          setUserPhotos(prev => ({ ...prev, headshot: null }));
                        }
                      }}
                    />
                  ) : headshotLoadError ? (
                    <div className="upload-placeholder" style={{ color: '#dc3545', border: '2px dashed #dc3545' }}>
                      <span className="upload-icon">⚠️</span>
                      <span>Image not found. Please upload a new headshot.</span>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <span className="upload-icon">📸</span>
                      <span>Headshot Photo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={headshotInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'headshot')}
                  style={{ display: 'none' }}
                />
              </label>
              {/* Edit icon overlay - only show when image exists */}
              {(userPhotos.headshot || headshotPreview) && (
                <button
                  className="edit-icon-overlay"
                  onClick={() => handleEditIconClick('headshot')}
                  disabled={uploading && uploadingType === 'headshot'}
                  title="Click to change headshot"
                >
                  {uploading && uploadingType === 'headshot' ? (
                    <span className="edit-icon-loading">⏳</span>
                  ) : (
                    <span className="edit-icon">✏️</span>
                  )}
                </button>
              )}
            </div>
          </div>
          
          <div className="upload-item">
            <div className="upload-box-wrapper">
              <label className="upload-label">
                <div className="upload-box">
                  {fullBodyPreview ? (
                    <img src={fullBodyPreview} alt="Full body preview" className="preview-image" key={`fullbody-preview-${imageVersion}`} />
                  ) : userPhotos.fullbody ? (
                    <img 
                      ref={fullbodyImageRef}
                      src={(() => {
                        if (!userPhotos.fullbody) return '';
                        // Ensure we have a valid URL
                        let baseUrl = userPhotos.fullbody;
                        // Remove any existing query parameters
                        if (baseUrl.includes('?')) {
                          baseUrl = baseUrl.split('?')[0];
                        }
                        // Ensure it's a full URL (add protocol if missing)
                        if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                          // If it's a relative URL, make it absolute
                          if (baseUrl.startsWith('/')) {
                            baseUrl = window.location.origin + baseUrl;
                          } else {
                            baseUrl = window.location.origin + '/' + baseUrl;
                          }
                        }
                        const finalUrl = baseUrl + '?v=' + imageVersion + '&r=' + imageRefreshKey;
                        console.log('Full body image URL:', finalUrl);
                        return finalUrl;
                      })()}
                      alt="Full body" 
                      className="preview-image" 
                      key={`fullbody-${imageVersion}-${imageRefreshKey}-${userPhotos.fullbody}`}
                      style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
                      onLoad={() => {
                        // Image loaded successfully
                        console.log('✅ Full body image loaded successfully:', userPhotos.fullbody);
                        setFullbodyLoadError(false);
                        setFullbodyRetryCount(0);
                        if (fullbodyImageRef.current) {
                          fullbodyImageRef.current.style.opacity = '1';
                        }
                      }}
                      onError={(e) => {
                        // Limit retries to prevent infinite loops
                        if (fullbodyRetryCount < 2) {
                          console.error('❌ Full body image failed to load, retrying...', userPhotos.fullbody);
                          setFullbodyRetryCount(prev => prev + 1);
                          if (userPhotos.fullbody) {
                            let baseUrl = userPhotos.fullbody.split('?')[0];
                            if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                              if (baseUrl.startsWith('/')) {
                                baseUrl = window.location.origin + baseUrl;
                              } else {
                                baseUrl = window.location.origin + '/' + baseUrl;
                              }
                            }
                            const retryUrl = baseUrl + '?v=' + Date.now() + '&r=' + (imageRefreshKey + 1);
                            console.log('Retrying with URL:', retryUrl);
                            e.target.src = retryUrl;
                          }
                        } else {
                          // Max retries reached, show error state
                          console.error('❌ Full body image failed to load after retries. File may not exist:', userPhotos.fullbody);
                          console.error('❌ This might be an old image URL. Please upload a new image.');
                          setFullbodyLoadError(true);
                          // Clear the failed URL so user can upload a new one
                          setUserPhotos(prev => ({ ...prev, fullbody: null }));
                        }
                      }}
                    />
                  ) : fullbodyLoadError ? (
                    <div className="upload-placeholder" style={{ color: '#dc3545', border: '2px dashed #dc3545' }}>
                      <span className="upload-icon">⚠️</span>
                      <span>Image not found. Please upload a new full body photo.</span>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <span className="upload-icon">👤</span>
                      <span>Full Body Photo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fullbodyInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'fullbody')}
                  style={{ display: 'none' }}
                />
              </label>
              {/* Edit icon overlay - only show when image exists */}
              {(userPhotos.fullbody || fullBodyPreview) && (
                <button
                  className="edit-icon-overlay"
                  onClick={() => handleEditIconClick('fullbody')}
                  disabled={uploading && uploadingType === 'fullbody'}
                  title="Click to change full body photo"
                >
                  {uploading && uploadingType === 'fullbody' ? (
                    <span className="edit-icon-loading">⏳</span>
                  ) : (
                    <span className="edit-icon">✏️</span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="upload-actions">
          <Link to="/edit-account" className="btn btn-secondary">
            Edit Profile
          </Link>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success" style={{ 
        background: '#d4edda', 
        color: '#155724', 
        padding: '12px 20px', 
        borderRadius: '4px', 
        marginBottom: '20px',
        border: '1px solid #c3e6cb'
      }}>{success}</div>}

      {Array.isArray(profiles) && profiles.length > 0 && (
        <div className="profiles-grid">
          {profiles.map(profile => (
            <div key={profile.id} className="profile-card">
              <div className="profile-header">
                <h3>{profile.firstName} {profile.lastName}</h3>
                <div>
                  <Link to={`/edit/${profile.id}`} className="btn btn-secondary">
                    Edit
                  </Link>
                  <button 
                    onClick={() => deleteProfile(profile.id)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="profile-info">
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{profile.email}</span>
                </div>
                
                {profile.phone && (
                  <div className="info-item">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">{profile.phone}</span>
                  </div>
                )}
                
                {profile.dateOfBirth && (
                  <div className="info-item">
                    <span className="info-label">Date of Birth:</span>
                    <span className="info-value">
                      {new Date(profile.dateOfBirth).toLocaleDateString()}
                    </span>
                  </div>
                )}
                
                {profile.address && (
                  <div className="info-item">
                    <span className="info-label">Address:</span>
                    <span className="info-value">
                      {profile.address}
                      {profile.city && `, ${profile.city}`}
                      {profile.state && `, ${profile.state}`}
                      {profile.zipCode && ` ${profile.zipCode}`}
                      {profile.country && `, ${profile.country}`}
                    </span>
                  </div>
                )}
                
                {profile.skills && (
                  <div className="info-item">
                    <span className="info-label">Skills:</span>
                    <span className="info-value">{profile.skills}</span>
                  </div>
                )}
                
                {profile.experience && (
                  <div className="info-item">
                    <span className="info-label">Experience:</span>
                    <span className="info-value">{profile.experience}</span>
                  </div>
                )}
                
                {profile.portfolio_url && (
                  <div className="info-item">
                    <span className="info-label">Portfolio:</span>
                    <span className="info-value">
                      <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer">
                        View Portfolio
                      </a>
                    </span>
                  </div>
                )}
                
                {profile.hourly_rate && (
                  <div className="info-item">
                    <span className="info-label">Hourly Rate:</span>
                    <span className="info-value">${profile.hourly_rate}/hour</span>
                  </div>
                )}
                
                <div className="info-item">
                  <span className="info-label">Availability:</span>
                  <span className={`info-value availability-${profile.availability}`}>
                    {profile.availability.charAt(0).toUpperCase() + profile.availability.slice(1)}
                  </span>
                </div>
                
                <div className="info-item">
                  <span className="info-label">Created:</span>
                  <span className="info-value">
                    {new Date(profile.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserProfile;
